import { Controller } from "@hotwired/stimulus";

type ChatPane = "ai" | "team" | "user";

type HtmxBeforeSwapEvent = CustomEvent<{
  requestConfig?: {
    parameters?: Record<string, string | number>;
    path?: string;
  };
  xhr?: {
    responseURL?: string;
  };
  shouldSwap?: boolean;
  target?: EventTarget | null;
}>;

type HtmxConfigRequestEvent = CustomEvent<{
  path?: string;
}>;

const HTMX_REQUEST_TERMINAL_EVENTS = [
  "htmx:afterRequest",
  "htmx:responseError",
  "htmx:sendError",
  "htmx:timeout",
  "htmx:swapError",
] as const;

export default class extends Controller<HTMLElement> {
  private refreshAfterStaleResponseTimerId: number | null = null;
  private readonly capturePaneIntent = (event: Event): void => {
    const target = event.target;

    if (!(target instanceof Element)) {
      return;
    }

    const ticketRow = target.closest<HTMLElement>('[data-ticket-selection-target="row"]');
    if (ticketRow && this.element.contains(ticketRow)) {
      // A background list morph can restore server-rendered row URLs while a
      // chat-tab request is still pending. Repair the clicked row in capture
      // phase so the selection request and pushed URL use the pane the user
      // most recently chose.
      this.syncTicketSelectionLink(ticketRow, this.requestedPane());
      return;
    }

    const tab = target.closest<HTMLElement>('[role="tab"][data-chat-pane]');
    if (tab && this.element.contains(tab)) {
      // HTMX handles the request later in the same click propagation. Prevent
      // the anchor's native fallback here so a concurrently morphed tab cannot
      // navigate using stale href state while the HTMX request is in flight.
      if (event.type === "click" && tab.hasAttribute("hx-get")) {
        event.preventDefault();
      }
      this.rememberPane(tab);
    }
  };

  private readonly guardChatSwap = (event: Event): void => {
    const detail = (event as HtmxBeforeSwapEvent).detail;
    const target = detail?.target;

    if (!(target instanceof HTMLElement) || target.id !== "tickets-chat-fragment") {
      return;
    }

    const requestPath = detail?.requestConfig?.path || detail?.xhr?.responseURL || "";
    const responsePane = this.chatPaneForPath(requestPath);
    const requestedPane = this.requestedPane();
    const responseTicketId = String(
      detail?.requestConfig?.parameters?.ticketId ||
        this.ticketIdForPath(detail?.xhr?.responseURL || requestPath),
    );
    const requestedTicketId = this.element.dataset.currentTicketId || "";

    const responseBelongsToPreviousTicket =
      Boolean(responseTicketId) &&
      Boolean(requestedTicketId) &&
      responseTicketId !== requestedTicketId;

    if (
      responseBelongsToPreviousTicket ||
      (responsePane && responsePane !== requestedPane) ||
      this.selectionSwapWouldDiscardAttachments(requestPath, target)
    ) {
      detail.shouldSwap = false;
      event.preventDefault();
      if (responseBelongsToPreviousTicket) {
        this.scheduleCurrentChatRefresh();
      }
    }
  };

  private readonly configureTicketSelectionRequest = (event: Event): void => {
    const detail = (event as HtmxConfigRequestEvent).detail;
    if (!detail?.path) {
      return;
    }

    const url = new URL(detail.path, window.location.origin);
    if (url.pathname !== "/TicketSelectionFragment") {
      return;
    }

    const pane = this.requestedPane();
    if (pane === "ai") {
      url.searchParams.delete("chatPane");
    } else {
      url.searchParams.set("chatPane", pane);
    }
    detail.path = `${url.pathname}${url.search}${url.hash}`;
  };

  private readonly recoverAfterStaleChatRequest = (event: Event): void => {
    const detail = (event as HtmxBeforeSwapEvent).detail;
    const requestPath = detail?.requestConfig?.path || detail?.xhr?.responseURL || "";

    if (!this.isChatFragmentPath(requestPath)) {
      return;
    }

    const requestTicketId = String(
      detail?.requestConfig?.parameters?.ticketId || this.ticketIdForPath(requestPath),
    );
    const currentTicketId = this.element.dataset.currentTicketId || "";

    if (requestTicketId && currentTicketId && requestTicketId !== currentTicketId) {
      this.scheduleCurrentChatRefresh();
    }
  };

  connect(): void {
    this.element.addEventListener("pointerdown", this.capturePaneIntent, true);
    this.element.addEventListener("click", this.capturePaneIntent, true);
    document.body.addEventListener("htmx:configRequest", this.configureTicketSelectionRequest);
    document.body.addEventListener("htmx:beforeSwap", this.guardChatSwap);
    HTMX_REQUEST_TERMINAL_EVENTS.forEach((eventName) => {
      document.body.addEventListener(eventName, this.recoverAfterStaleChatRequest);
    });
  }

  disconnect(): void {
    this.element.removeEventListener("pointerdown", this.capturePaneIntent, true);
    this.element.removeEventListener("click", this.capturePaneIntent, true);
    document.body.removeEventListener("htmx:configRequest", this.configureTicketSelectionRequest);
    document.body.removeEventListener("htmx:beforeSwap", this.guardChatSwap);
    HTMX_REQUEST_TERMINAL_EVENTS.forEach((eventName) => {
      document.body.removeEventListener(eventName, this.recoverAfterStaleChatRequest);
    });
    if (this.refreshAfterStaleResponseTimerId !== null) {
      window.clearTimeout(this.refreshAfterStaleResponseTimerId);
      this.refreshAfterStaleResponseTimerId = null;
    }
  }

  selectPane(event: Event): void {
    const tab = event.currentTarget;

    if (!(tab instanceof HTMLElement)) {
      return;
    }

    this.rememberPane(tab);
  }

  private rememberPane(tab: HTMLElement): void {
    const pane = tab.dataset.chatPane;
    if (pane === "ai" || pane === "team" || pane === "user") {
      this.element.dataset.requestedChatPane = pane;
      this.syncTicketSelectionLinks(pane);
    }
  }

  private syncTicketSelectionLinks(pane: ChatPane): void {
    this.element
      .querySelectorAll<HTMLElement>('[data-ticket-selection-target="row"]')
      .forEach((row) => {
        this.syncTicketSelectionLink(row, pane);
      });
  }

  private syncTicketSelectionLink(row: HTMLElement, pane: ChatPane): void {
    this.updateSelectionFragmentUrl(row, "hx-get", pane);
    this.updateSelectionPageUrl(row, "href", pane);
    this.updateSelectionPageUrl(row, "hx-push-url", pane);
    this.updateFragmentPreloadUrls(row, pane);
  }

  private updateFragmentPreloadUrls(row: HTMLElement, pane: ChatPane): void {
    const ticketId = row.dataset.ticketId;
    if (!ticketId) {
      return;
    }

    const detailUrl = `/TicketDetailFragment?ticketId=${encodeURIComponent(ticketId)}`;
    const chatUrl = new URL(
      pane === "team" ? "/TicketTeamChatFragment" : "/TicketChatFragment",
      window.location.origin,
    );
    chatUrl.searchParams.set("ticketId", ticketId);
    if (pane === "user") {
      chatUrl.searchParams.set("chatPane", "user");
    }

    row.setAttribute(
      "data-hx-preload-urls",
      `${detailUrl} ${chatUrl.pathname}${chatUrl.search}`,
    );
  }

  private updateSelectionFragmentUrl(row: HTMLElement, attribute: string, pane: ChatPane): void {
    this.updateUrlAttribute(row, attribute, (url) => {
      if (pane === "ai") {
        url.searchParams.delete("chatPane");
      } else {
        url.searchParams.set("chatPane", pane);
      }
    });
  }

  private updateSelectionPageUrl(row: HTMLElement, attribute: string, pane: ChatPane): void {
    this.updateUrlAttribute(row, attribute, (url) => {
      url.pathname = pane === "team" ? "/ShowTicketTeamChat" : "/ShowTicket";
      if (pane === "user") {
        url.searchParams.set("chatPane", "user");
      } else {
        url.searchParams.delete("chatPane");
      }
    });
  }

  private updateUrlAttribute(
    element: HTMLElement,
    attribute: string,
    update: (url: URL) => void,
  ): void {
    const value = element.getAttribute(attribute);
    if (!value || value === "true" || value === "false") {
      return;
    }

    const url = new URL(value, window.location.origin);
    update(url);
    element.setAttribute(attribute, `${url.pathname}${url.search}${url.hash}`);
  }

  private requestedPane(): ChatPane {
    const pane = this.element.dataset.requestedChatPane || this.element.dataset.chatPane;

    if (pane === "team" || pane === "user") {
      return pane;
    }

    return "ai";
  }

  private chatPaneForPath(path: string): ChatPane | null {
    if (!path) {
      return null;
    }

    const url = new URL(path, window.location.origin);
    if (url.pathname === "/TicketSelectionFragment") {
      const pane = url.searchParams.get("chatPane");
      return pane === "team" || pane === "user" ? pane : "ai";
    }

    if (url.pathname === "/TicketTeamChatFragment") {
      return "team";
    }

    if (url.pathname !== "/TicketChatFragment") {
      return null;
    }

    return url.searchParams.get("chatPane") === "user" ? "user" : "ai";
  }

  private ticketIdForPath(path: string): string {
    if (!path) {
      return "";
    }

    try {
      return new URL(path, window.location.origin).searchParams.get("ticketId") || "";
    } catch {
      return "";
    }
  }

  private isChatFragmentPath(path: string): boolean {
    if (!path) {
      return false;
    }

    try {
      const pathname = new URL(path, window.location.origin).pathname;
      return pathname === "/TicketChatFragment" || pathname === "/TicketTeamChatFragment";
    } catch {
      return false;
    }
  }

  private scheduleCurrentChatRefresh(): void {
    if (this.refreshAfterStaleResponseTimerId !== null) {
      window.clearTimeout(this.refreshAfterStaleResponseTimerId);
    }

    // beforeSwap still runs inside the obsolete request's sync section. Wait
    // until the next task so HTMX has released it, then request the registration
    // that belongs to the optimistically selected ticket.
    this.refreshAfterStaleResponseTimerId = window.setTimeout(() => {
      this.refreshAfterStaleResponseTimerId = null;
      const currentTicketId = this.element.dataset.currentTicketId || "";
      const renderedTicketId =
        document.getElementById("tickets-chat-fragment")?.dataset.ticketId || "";
      if (currentTicketId && renderedTicketId === currentTicketId) {
        return;
      }
      const registration = document.getElementById("tickets-chat-auto-refresh-registration");
      const registrationTicketId = this.ticketIdForPath(
        registration?.getAttribute("hx-get") || "",
      );
      if (!currentTicketId || registrationTicketId !== currentTicketId) {
        // The ticket-selection response has not installed the new registration
        // yet. Dispatching on the old one would loop requests for the previous
        // ticket; the new registration's own load/key-change trigger will run
        // once it arrives.
        return;
      }
      registration?.dispatchEvent(new CustomEvent("ticket-chat-refresh", { bubbles: true }));
    }, 0);
  }

  private selectionSwapWouldDiscardAttachments(path: string, target: HTMLElement): boolean {
    if (!path || new URL(path, window.location.origin).pathname !== "/TicketSelectionFragment") {
      return false;
    }

    const input = target.querySelector<HTMLInputElement>('[data-testid="ticket-chat-attachments-input"]');
    return Boolean(input?.files?.length);
  }
}
